import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAdminUsers } from 'app/javascript/queries/useAdmin';
import { useDebouncedValue } from 'app/javascript/hooks/useDebouncedValue';
import AdminPanel from 'app/javascript/components/admin/AdminPanel';
import AdminPagination from 'app/javascript/components/admin/AdminPagination';
import AdminSearchField from 'app/javascript/components/admin/AdminSearchField';
import UserDevices from 'app/javascript/components/admin/UserDevices';
import { Badge } from 'app/javascript/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'app/javascript/components/ui/table';
import { ROUTES } from 'app/javascript/constants/routes';
import { formatBRDateTime } from 'app/javascript/utils/dates';

export default function RecentUsersTable() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading, isError } = useAdminUsers({ search: debouncedSearch, page });
  const users = data?.users ?? [];

  // A new query invalidates the current page number — page 3 of the old result
  // set means nothing in the new one.
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className="space-y-3">
      <AdminSearchField
        value={search}
        onChange={handleSearch}
        placeholder="Buscar por nome, e-mail ou CPF"
        resultCount={data?.pagy.count}
      />
      <AdminPanel
        isLoading={isLoading}
        isError={isError}
        isEmpty={users.length === 0}
        emptyMessage={
          debouncedSearch
            ? 'Nenhum usuário encontrado para esta busca.'
            : 'Nenhum usuário criado ainda.'
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Academias</TableHead>
              <TableHead>Dispositivos</TableHead>
              <TableHead>Criado em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {user.name || '—'}
                    {user.lgpd_erased && <Badge variant="destructive">Removido (LGPD)</Badge>}
                  </span>
                  {/* Erased accounts 404 on /p/:slug, so no link is offered for them.
                      New tab on purpose: the public profile is what visitors see. */}
                  {user.slug && !user.lgpd_erased && (
                    <Link
                      to={ROUTES.PUBLIC_PROFILE}
                      params={{ slug: user.slug }}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="block text-xs text-muted-foreground hover:text-foreground hover:underline"
                    >
                      /p/{user.slug}
                    </Link>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{user.email || '—'}</TableCell>
                <TableCell className="text-muted-foreground">
                  {user.companies.length > 0 ? user.companies.join(', ') : '—'}
                </TableCell>
                <TableCell>
                  <UserDevices devices={user.devices} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatBRDateTime(user.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AdminPanel>
      <AdminPagination pagy={data?.pagy} onPageChange={setPage} />
    </div>
  );
}
